import React, { useRef, useState } from 'react';
import {
    API,
    ECode,
    PRODUCT,
    SITE_ROUTER,
    SUITE_ERROR,
    SuiteId,
    TRACKER,
    URL_PARAMETER,
    VIP_ERROR
} from '~/constants';
import { Alert, Dialog, Message } from '@fx-ui/fine-design';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { useBusinessLink, useStores } from '~/hooks';
import {
    assign,
    filter,
    get,
    getApi,
    getErrorMsg,
    getSiteRouter,
    getUrlParameter,
    getVipErrMsg,
    handleTemplateRemoved,
    has,
    isEmpty,
    isTemplateExternalLink,
    map,
    openInNewTab,
    post,
    promiseQueue,
    redirect,
    textLimit,
    tracker,
    includes
} from '~/utils';
import Intl from '@fx-ui/intl';
import PluginInstall, { IInstallPlugin, IPluginInstallRef } from './components/plugin-install';
import { PlatformVipAddOnKey } from '~/interfaces';
import { checkSuiteAccessible, getProductCenterUrl } from '@fx-ui/platform-sdk';

const BOTH_COUNT = 3; // 容许同时发送的最多请求
/**
 * 安装单个应用和多个应用
 */
const useAppInstall = (
    onShowUpgrade: (config: { bufferState: { code: number | string } }) => void
) => {
    const { corpUserStore } = useStores();
    const { purchaseLink } = useBusinessLink();
    const isCorpUserInit = corpUserStore.isCurrentCorpExist;
    const [installing, setInstalling] = useState<boolean>(false); // 单个安装中
    const [multiInstalling, setMultiInstalling] = useState<boolean>(false); // 批量安装中
    const contentRef = useRef<IPluginInstallRef>(null);

    const suiteAccessCheck = () => {
        return new Promise((resolve, reject) => {
            checkSuiteAccessible(SuiteId.APP_BUILDER, {
                customHandleErrorCodes: SUITE_ERROR
            })
                .then(() => {
                    resolve(null);
                })
                .catch((err) => {
                    const errorCode = get(err, 'response.data.code');
                    if (includes(SUITE_ERROR, errorCode)) {
                        switch (errorCode) {
                            case ECode.SUITE_CONSENT_NOT_ACTIVATED:
                            case ECode.TENANT_SUITE_CONSENT_NOT_FOUND:
                            case ECode.SUITE_CONSENT_CORP_SCOPE_MANAGED_BY_INTEGRATE_SYNC:
                                if (corpUserStore.hasSysAdmin) {
                                    Alert.openAlert({
                                        type: 'warning',
                                        message: Intl.get('vip.error.app.builder.enabled.admin'),
                                        title: Intl.get('template.install.fail'),
                                        text4Cancel: Intl.get('close'),
                                        text4Ok: Intl.get('template.install.go.product.center'),
                                        props4Ok: { type: 'primary', danger: false },
                                        onOk: () => {
                                            redirect(getProductCenterUrl(corpUserStore.tenantId));
                                        }
                                    });
                                } else {
                                    Alert.openAlert({
                                        type: 'warning',
                                        message: Intl.get('vip.error.app.builder.enabled'),
                                        title: Intl.get('template.install.fail'),
                                        text4Cancel: null,
                                        text4Ok: Intl.get('iSee')
                                    });
                                }
                                break;
                            case ECode.SUITE_AUTHENTICATION_OUT_OF_SCOPE:
                            case ECode.SUITE_EXCEED_SCOPE_MEMBER_NOT_SA:
                            case ECode.SUITE_EXCEED_SCOPE_EXTERNAL_MEMBER_NOT_SA:
                                Alert.openAlert({
                                    type: 'warning',
                                    message: Intl.get('vip.error.app.builder.range'),
                                    title: Intl.get('template.install.fail'),
                                    text4Cancel: null,
                                    text4Ok: Intl.get('iSee')
                                });
                                break;
                            case ECode.SUITE_AUTHENTICATION_OUT_OF_SCOPE_IS_SA:
                            case ECode.SUITE_EXCEED_SCOPE_MEMBER_IS_SA:
                            case ECode.SUITE_EXCEED_SCOPE_EXTERNAL_MEMBER_IS_SA:
                                Alert.openAlert({
                                    type: 'warning',
                                    message: Intl.get('vip.error.app.builder.range.admin'),
                                    title: Intl.get('template.install.fail'),
                                    text4Cancel: Intl.get('close'),
                                    text4Ok: Intl.get('template.install.go.product.center'),
                                    props4Ok: { type: 'primary', danger: false },
                                    onOk: () => {
                                        redirect(getProductCenterUrl(corpUserStore.tenantId));
                                    }
                                });
                                break;
                            default:
                                break;
                        }
                        reject(err);
                    } else {
                        resolve(null);
                    }
                });
        });
    };

    const installApp = (appId: string, withData: boolean, extra?: Record<string, any>) => {
        if (installing) {
            return Promise.resolve();
        }
        const data = { forkId: appId, hasData: withData };
        setInstalling(true);
        if (extra) {
            assign(data, extra);
        }
        return suiteAccessCheck()
            .then(() => {
                return appPluginInstall([appId])
                    .then(() => {
                        return post(getApi(API.app.install), data, {
                            customHandleErrorCodes: [
                                1030,
                                2014,
                                2056,
                                11007,
                                2059,
                                2004,
                                2061,
                                2062,
                                44003,
                                VIP_ERROR.APP
                            ]
                        })
                            .catch((error) => {
                                const data = get(error, 'response.data');
                                const code = get(data, 'code', 1);
                                if (VIP_ERROR.APP === code) {
                                    onShowUpgrade({ bufferState: data });
                                    return;
                                }
                                // 模板安装失败
                                switch (code) {
                                    case 1030:
                                        redirect(
                                            getSiteRouter(SITE_ROUTER.AUTHENTICATION.REGISTER)
                                        );
                                        break;
                                    case 2014:
                                    case 11007:
                                        Alert.openAlert({
                                            type: 'warning',
                                            message: getErrorMsg(data),
                                            title: Intl.get('template.install.fail'),
                                            text4Ok: Intl.get('corp.create.join'),
                                            onOk: () => {
                                                redirect(getSiteRouter(SITE_ROUTER.DASHBOARD));
                                            }
                                        });
                                        break;
                                    case 2056:
                                        Alert.openAlert({
                                            type: 'warning',
                                            message: getErrorMsg(data),
                                            title: Intl.get('template.install.fail.flow'),
                                            text4Cancel: Intl.get('iSee'),
                                            text4Ok: null
                                        });
                                        break;
                                    case 2059:
                                        Alert.openAlert({
                                            type: 'warning',
                                            title: getErrorMsg(data),
                                            message: Intl.get('template.install.fail.automation'),
                                            text4Cancel: Intl.get('iSee'),
                                            text4Ok: null
                                        });
                                        break;
                                    case 2061:
                                        Alert.openAlert({
                                            type: 'warning',
                                            title: Intl.get('install.fail.beta.title'),
                                            text4Cancel: null,
                                            text4Ok: Intl.get('iSee')
                                        });
                                        break;
                                    case 2062:
                                        Alert.openAlert({
                                            type: 'warning',
                                            title: Intl.get('install.fail.offline.title'),
                                            text4Cancel: null,
                                            text4Ok: Intl.get('iSee')
                                        });
                                        break;
                                    case 2004:
                                        handleTemplateRemoved();
                                        break;
                                    case 44003:
                                        Alert.openAlert({
                                            type: 'warning',
                                            title: Intl.get('template.install.fail.official.title'),
                                            message: Intl.get('template.install.fail.official.msg'),
                                            text4Cancel: null,
                                            text4Ok: Intl.get('iSee')
                                        });
                                        break;
                                    default:
                                        Alert.openAlert({
                                            type: 'error',
                                            message: getErrorMsg(data),
                                            title: Intl.get('template.install.fail'),
                                            text4Cancel: Intl.get('vip.upgrade.no'),
                                            text4Ok: Intl.get('vip.upgrade.yes'),
                                            onOk: () => {
                                                openInNewTab(purchaseLink, '_blank');
                                            }
                                        });
                                        break;
                                }
                            })
                            .finally(() => {
                                setInstalling(false);
                            });
                    })
                    .catch(() => {
                        setInstalling(false);
                    });
            })
            .catch(() => {
                setInstalling(false);
            });
    };

    /**
     * 批量安装推荐应用
     *
     * @param appList 批量安装列表
     * @param successEach 单个安装成功的附加行为,返回安装的post参数和回调值
     */
    const batchInstallApp = (
        appList: { name: string; _id: string }[],
        successEach?: (appItem: { name: string; _id: string }, res: any) => void
    ) => {
        if (multiInstalling) {
            return Promise.resolve();
        }
        setMultiInstalling(true);
        return appPluginInstall(map(appList, '_id')).then(() => {
            return promiseQueue(
                BOTH_COUNT,
                map(appList, (appItem) => {
                    return () => {
                        return post(
                            getApi(API.app.install),
                            {
                                forkId: get(appItem, '_id'),
                                hasData: true,
                                channel: 'ad' // 广告或推荐位安装
                            },
                            { customHandleHttpCodes: [400] }
                        ).then(
                            (res) => {
                                successEach && successEach(appItem, res);
                                // 安装成功
                                Message.add({
                                    messageType: 'toast',
                                    type: 'success',
                                    message: Intl.get('app.install.success.name', {
                                        name: textLimit(appItem.name, 12)
                                    })
                                });
                            },
                            (err) => {
                                const error = get(err, 'response.data');
                                if (VIP_ERROR.APP === error?.code) {
                                    onShowUpgrade({ bufferState: error });
                                    return;
                                }
                                Message.add({
                                    messageType: 'toast',
                                    type: 'warning',
                                    message: Intl.get('app.install.fail.name', {
                                        name: textLimit(appItem.name, 12)
                                    })
                                });
                            }
                        );
                    };
                })
            ).then(() => {
                setMultiInstalling(false);
            });
        });
    };

    /**
     * 获取安装应用所携带的插件列表
     *
     * @param apps
     */
    const appPluginInstall = (apps: string[]) => {
        return new Promise<void>((resolve, reject) => {
            post(
                getApi(API.app.get_install_plugin),
                {
                    app_id_list: apps
                },
                {
                    customHandleErrorCodes: [2004]
                }
            ).then(
                (res) => {
                    const plugins: IInstallPlugin[] = filter(
                        get(res, 'data.plugin_list', []),
                        (v) => {
                            return !v.is_installed && !has(v, ['_id', 'name', 'icon']);
                        }
                    );
                    if (!isEmpty(plugins)) {
                        addDialog(plugins, resolve, reject, apps);
                    } else {
                        resolve();
                    }
                },
                () => {
                    resolve();
                }
            );
        });
    };

    const pluginVersionCheck = () => {
        return corpUserStore.hasPlatformVipFunction(PlatformVipAddOnKey.open);
    };

    const alertPlugin = (resolve: (value?: any) => void, installDialog: any) => {
        const finish = () => {
            resolve();
            Alert.closeAlert();
            Dialog.hide(installDialog);
        };
        const { title, msg, doc, allowAddon } = getVipErrMsg({
            corpUserStore,
            code: ECode.OPEN_PLATFORM_UNAVAILABLE
        });
        Alert.openAlert({
            title,
            message: msg,
            text4Ok: allowAddon ? Intl.get('vip.purchase.yes') : Intl.get('vip.upgrade.yes'),
            text4Cancel: allowAddon ? Intl.get('vip.purchase.no') : Intl.get('vip.upgrade.no'),
            onOk: () => {
                openInNewTab(purchaseLink);
                finish();
            },
            onCancel: () => {
                finish();
            },
            footerLeft: (
                <Hyperlink
                    underline={false}
                    onClick={() => {
                        doc && openInNewTab(doc);
                    }}
                >
                    {Intl.get('vip.advancedFeatures')}
                </Hyperlink>
            )
        });
    };

    /**
     * 插件安装选择
     *
     * @param plugins
     * @param resolve
     * @param reject
     */
    const addDialog = (
        plugins: IInstallPlugin[],
        resolve: (value?: any) => void,
        reject: () => void,
        appIds: string[]
    ) => {
        const trackerExt = {
            p1: appIds.join(','),
            p2: map(plugins, '_id').join('/')
        };
        const installDialog = Dialog.add({
            className: 'fx-plugin-install-dialog',
            content: <PluginInstall appIds={appIds} plugins={plugins} ref={contentRef} />,
            title: Intl.get('plugin.install'),
            text4Cancel: Intl.get('do.not.install'),
            width: 600,
            height: 480,
            onOk: () => {
                const pluginInstallMap = contentRef.current && contentRef.current.getValue();
                const handleFinish = () => {
                    resolve();
                    Dialog.hide(installDialog);
                };
                if (!isEmpty(pluginInstallMap)) {
                    tracker(TRACKER.PLUGIN_INSTALL_CONFIRM, trackerExt);
                    if (!corpUserStore.hasOpenPlatformManager && isCorpUserInit) {
                        const isOpenPlatformManagerEnabled =
                            corpUserStore.isTenantModuleEnabled('open_platform_manager');
                        const alertContent = isOpenPlatformManagerEnabled
                            ? {
                                  title: Intl.get(
                                      'fxp.base.workspace.tmpl_center.plugin_skip_install.ttl'
                                  ),
                                  message: Intl.get(
                                      'fxp.base.workspace.tmpl_center.app_install_skip_plugin.tip'
                                  ),
                                  text4Ok: Intl.get(
                                      'fxp.base.workspace.tmpl_center.continue_install.btn'
                                  )
                              }
                            : {
                                  title: Intl.get('openPlatform.install.auth.require'),
                                  text4Ok: Intl.get('iSee')
                              };
                        Alert.openAlert({
                            type: 'warning',
                            ...alertContent,
                            text4Cancel: null,
                            onOk: () => {
                                handleFinish();
                                Alert.closeAlert();
                            }
                        });
                    } else if (!pluginVersionCheck() && isCorpUserInit) {
                        alertPlugin(resolve, installDialog);
                    } else {
                        return batchInstallPlugins(pluginInstallMap!).then(() => {
                            handleFinish();
                        });
                    }
                } else {
                    handleFinish();
                }
            },
            onCancel: (_e, effect) => {
                if (effect === 'cancel') {
                    tracker(TRACKER.PLUGIN_INSTALL_JUMP, trackerExt);
                    resolve();
                    Dialog.hide(installDialog);
                }
                if (effect === 'close') {
                    Dialog.hide(installDialog);
                    reject();
                }
            }
        });
    };

    /**
     * 批量安装插件
     *
     * @param pluginInstallMap
     */
    const batchInstallPlugins = (pluginInstallMap: Record<string, Record<string, any>>) => {
        const pluginInstallList = Object.keys(pluginInstallMap);
        return promiseQueue(
            BOTH_COUNT,
            map(pluginInstallList, (pluginId) => {
                return () => {
                    return post(
                        getApi(API.open.plugin_install),
                        {
                            plugin_id: pluginId
                        },
                        {
                            customHandleHttpCodes: [400]
                        }
                    ).then(
                        () => {
                            tracker(TRACKER.PLUGIN_INSTALL, {
                                p1: get(pluginInstallMap, [pluginId, 'name']),
                                p2: pluginId
                            });
                            Message.add({
                                messageType: 'toast',
                                type: 'success',
                                message: Intl.get('plugin.install.success.name', {
                                    name: textLimit(get(pluginInstallMap, [pluginId, 'name']), 12)
                                })
                            });
                        },
                        (err) => {
                            const errorData = get(err, 'response.data');
                            const errorCode = get(errorData, 'code');
                            let msg = '';
                            if (errorCode === 17008) {
                                msg = Intl.get('vip.error.17008.integration.memMsg');
                            } else {
                                msg = getErrorMsg(errorData);
                            }
                            Message.add({
                                messageType: 'toast',
                                type: 'warning',
                                message: msg
                            });
                        }
                    );
                };
            })
        );
    };

    const appInstalled = (res: any, appId: string, withData: boolean, isDetail: boolean) => {
        if (isDetail) {
            tracker(TRACKER.FA_DETAIL_INSTALL_CLICK, {
                p1: PRODUCT,
                p2: appId,
                p3: withData ? 'keep' : 'no_keep'
            });
        } else {
            tracker(TRACKER.APP_INSTALL);
            const utmSrc = getUrlParameter(URL_PARAMETER.UTM_SRC);
            if (isTemplateExternalLink() && utmSrc) {
                tracker(TRACKER.FA_SHARE_LINK_INSTALL, {
                    p1: utmSrc,
                    p2: appId
                });
            }
        }
        redirect(getSiteRouter(SITE_ROUTER.APP_URL, res.data.app._id));
    };

    return {
        appInstalling: installing,
        appMultiInstalling: multiInstalling,
        installApp,
        batchInstallApp,
        appInstalled
    };
};

export default useAppInstall;
